import { useCallback, useEffect, useRef, useState } from 'react';

import type { LayoutChangeEvent } from 'react-native';
import {
  Linking,
  ScrollView,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';

import { AboutSection } from '../../components/about-section';
import { ContactSection } from '../../components/contact-section';
import { EducationSection } from '../../components/education-section';
import { HomeHeader } from '../../components/home-header';
import { RecommendationsSection } from '../../components/recommendations-section';
import { StackSection } from '../../components/stack-section';
import { WorkExperienceSection } from '../../components/work-experience-section';
import { useCopyToClipboard } from '../../hooks/use-copy-to-clipboard';
import { colors } from '../../theme';
import { HomeSection } from '../../typescript/sections';
import { EMAIL, LINKEDIN_URL, PHONE } from '../../utils/constants';
import { normalizePhoneForUrl } from '../../utils/normalize-phone';
import { parseSectionFromHash } from '../../utils/parse-section-hash';
import { isIos, isWeb } from '../../utils/platform';

const CV_DOWNLOAD_PATH = '/aleksandar-kalapsazov-cv.pdf';

export default function Home() {
  const { width } = useWindowDimensions();
  const [webViewportWidth, setWebViewportWidth] = useState(1024);
  const viewportWidth = isWeb ? webViewportWidth : width;
  const isMobile = viewportWidth <= 809;
  const { copy: copyHeaderEmail, copied: headerEmailCopied } = useCopyToClipboard(EMAIL);
  const { copy: copyContactEmail, copied: contactEmailCopied } = useCopyToClipboard(EMAIL);
  const scrollRef = useRef<ScrollView | null>(null);
  const sectionOffsetsRef = useRef<Partial<Record<HomeSection, number>>>({});
  const pendingHashSectionRef = useRef<HomeSection | null>(null);

  const scrollToSection = useCallback((section: HomeSection, animated: boolean) => {
    const targetY = sectionOffsetsRef.current[section];
    if (typeof targetY === 'number') {
      scrollRef.current?.scrollTo({ y: Math.max(targetY - 16, 0), animated });
      pendingHashSectionRef.current = null;
      return;
    }

    pendingHashSectionRef.current = section;
  }, []);

  const setSectionOffset =
    (section: HomeSection) => (event: LayoutChangeEvent) => {
      const nextY = event.nativeEvent.layout.y;
      sectionOffsetsRef.current[section] = nextY;

      if (pendingHashSectionRef.current === section) {
        scrollRef.current?.scrollTo({ y: Math.max(nextY - 16, 0), animated: false });
        pendingHashSectionRef.current = null;
      }
    };

  useEffect(() => {
    if (!isWeb) {
      return;
    }

    const updateViewportWidth = () => {
      const nextWidth =
        window.innerWidth ||
        document.documentElement.clientWidth ||
        1024;
      setWebViewportWidth(nextWidth);
    };

    updateViewportWidth();
    window.addEventListener('resize', updateViewportWidth);

    return () => {
      window.removeEventListener('resize', updateViewportWidth);
    };
  }, []);

  useEffect(() => {
    if (!isWeb) {
      return;
    }

    const applyHash = () => {
      const section = parseSectionFromHash(window.location.hash);
      if (!section) {
        pendingHashSectionRef.current = null;
        return;
      }
      scrollToSection(section, false);
    };

    applyHash();
    window.addEventListener('hashchange', applyHash);

    return () => {
      window.removeEventListener('hashchange', applyHash);
    };
  }, [scrollToSection, width]);

  useEffect(() => {
    if (!isWeb) {
      return;
    }

    const section = parseSectionFromHash(window.location.hash);
    if (!section) {
      return;
    }

    const rafId = window.requestAnimationFrame(() => {
      const targetY = sectionOffsetsRef.current[section];
      if (typeof targetY === 'number') {
        scrollRef.current?.scrollTo({ y: Math.max(targetY - 16, 0), animated: false });
        pendingHashSectionRef.current = null;
      }
    });

    return () => window.cancelAnimationFrame(rafId);
  }, [width]);

  const onPressPhone = () => {
    void (async () => {
      const phone = normalizePhoneForUrl(PHONE);
      const telUrl = `tel:${phone}`;
      const faceTimeUrl = `facetime-audio:${phone}`;

      const canTel = await Linking.canOpenURL(telUrl);
      if (canTel) {
        await Linking.openURL(telUrl);
        return;
      }

      if (isIos) {
        const canFaceTime = await Linking.canOpenURL(faceTimeUrl);
        if (canFaceTime) {
          await Linking.openURL(faceTimeUrl);
        }
      }
    })();
  };

  const onPressSectionTitle =
    (section: HomeSection) => {
      if (isWeb) {
        const nextHash = `#${section}`;
        if (window.location.hash !== nextHash) {
          window.location.hash = nextHash;
          return;
        }
      }

      scrollToSection(section, true);
    };

  const onPressDownloadCv = useCallback(() => {
    if (isWeb && typeof document !== 'undefined') {
      const link = document.createElement('a');
      link.href = CV_DOWNLOAD_PATH;
      link.download = 'Aleksandar-Kalapsazov-CV.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    void Linking.openURL(CV_DOWNLOAD_PATH);
  }, []);

  return (
    <View style={styles.page}>
      <ScrollView
        ref={scrollRef}
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.shell}>
          <View style={[styles.content, isMobile && styles.contentMobile]}>
            <HomeHeader
              email={EMAIL}
              isEmailCopied={headerEmailCopied}
              isMobile={isMobile}
              linkedInUrl={LINKEDIN_URL}
              location="Sofia, Bulgaria"
              name="Alexander Kalapsazov"
              onCopyEmail={() => {
                void copyHeaderEmail();
              }}
              onPressDownloadCv={onPressDownloadCv}
              role="React Native Developer"
            />

            <AboutSection
              onLayout={setSectionOffset('about')}
              onPressTitle={() => onPressSectionTitle('about')}
            />

            <EducationSection
              onLayout={setSectionOffset('education')}
              onPressTitle={() => onPressSectionTitle('education')}
            />

            <WorkExperienceSection
              onLayout={setSectionOffset('work-experience')}
              onPressTitle={() => onPressSectionTitle('work-experience')}
            />

            <StackSection
              isMobile={isMobile}
              onLayout={setSectionOffset('stack')}
              onPressTitle={() => onPressSectionTitle('stack')}
            />

            <RecommendationsSection
              onLayout={setSectionOffset('recommendations')}
              onPressTitle={() => onPressSectionTitle('recommendations')}
            />

            <ContactSection
              copiedEmail={contactEmailCopied}
              email={EMAIL}
              isMobile={isMobile}
              linkedInUrl={LINKEDIN_URL}
              onCopyEmail={() => {
                void copyContactEmail();
              }}
              onLayout={setSectionOffset('contact')}
              onPressPhone={() => {
                onPressPhone();
              }}
              onPressTitle={() => onPressSectionTitle('contact')}
              phone={PHONE}
            />
          </View>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  page: {
    backgroundColor: colors.background,
    flex: 1,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    paddingTop: 0,
  },
  shell: {
    alignItems: 'center',
    width: '100%',
  },
  content: {
    maxWidth: 700,
    paddingBottom: 120,
    width: '100%',
  },
  contentMobile: {
    paddingHorizontal: 16,
  },
});
